import { useState, useCallback, useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import type { Card as CardType, CardStatus, LaneId, Owner, Priority } from "../../data/types";
import { StatusChip } from "./StatusChip";
import { OwnerTag } from "./OwnerTag";
import { PriorityDot } from "./PriorityDot";
import { Badge } from "./Badge";
import { Sheet, SheetHeader, SheetBody } from "./Sheet";

interface CardDetailProps {
  card: CardType | null;
  allCards: CardType[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (cardId: string, status: CardStatus) => void;
  onCardUpdate?: (cardId: string, fields: Partial<Pick<CardType, "title" | "description" | "notes" | "owner" | "priority" | "targetDate" | "tags" | "url" | "laneId">>) => void;
  onArchive?: (cardId: string) => void;
  onDelete?: (cardId: string) => void;
}

const transitions: CardStatus[] = ["planned", "in_progress", "blocked", "done", "archived"];
const owners: Owner[] = ["Albert", "Joel", "AI Wave", "Joint"];
const priorities: Priority[] = ["critical", "high", "medium", "low"];

const laneLabels: Record<string, string> = {
  objective_kpis: "Objectives & KPIs",
  offer_pricing: "Offer & Pricing",
  channels: "Channels",
  commercial_pipeline: "Commercial Pipeline",
  weekly_content: "Weekly Content",
  revops_tracking: "RevOps & Tracking",
  feedback_loop: "Feedback Loop",
};

const weekLabels: Record<number, string> = {
  0: "W0 — Pre-launch",
  1: "W1 — Launch",
  2: "W2 — Optimize",
  3: "W3 — Scale",
  4: "W4 — Convert",
};

export function CardDetail({ card, allCards, open, onOpenChange, onStatusChange, onCardUpdate, onArchive, onDelete }: CardDetailProps) {
  if (!card) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={card.title}>
      <SheetHeader onClose={() => onOpenChange(false)}>
        <PriorityDot priority={card.priority} />
        <EditableTitle
          value={card.title}
          onSave={(val) => onCardUpdate?.(card.id, { title: val })}
        />
      </SheetHeader>

      <SheetBody>
        <CardDetailContent
          card={card}
          allCards={allCards}
          onStatusChange={onStatusChange}
          onCardUpdate={onCardUpdate}
          onArchive={onArchive}
          onDelete={onDelete}
        />
      </SheetBody>
    </Sheet>
  );
}

// ── Inner content (uses hooks safely) ───────────────────────

interface CardDetailContentProps {
  card: CardType;
  allCards: CardType[];
  onStatusChange: (cardId: string, status: CardStatus) => void;
  onCardUpdate?: (cardId: string, fields: Partial<Pick<CardType, "title" | "description" | "notes" | "owner" | "priority" | "targetDate" | "tags" | "url" | "laneId">>) => void;
  onArchive?: (cardId: string) => void;
  onDelete?: (cardId: string) => void;
}

function CardDetailContent({ card, allCards, onStatusChange, onCardUpdate, onArchive, onDelete }: CardDetailContentProps) {
  const deps = card.dependsOn
    .map((id) => allCards.find((c) => c.id === id))
    .filter(Boolean) as CardType[];
  const dependents = allCards.filter((c) => c.dependsOn.includes(card.id));

  return (
    <div className="space-y-[var(--spacing-5)]">
      {/* Description — editable */}
      <EditableTextArea
        label="Description"
        value={card.description}
        placeholder="Add a description…"
        onSave={(val) => onCardUpdate?.(card.id, { description: val })}
      />

      {/* Status */}
      <Section label="Status">
        <div className="flex gap-[var(--spacing-1)] flex-wrap">
          {transitions.map((s) => (
            <StatusChip
              key={s}
              status={s}
              size="md"
              className={cn(card.status === s && "ring-2 ring-[var(--color-accent)]")}
              onClick={() => onStatusChange(card.id, s)}
            />
          ))}
        </div>
      </Section>

      {/* Details grid — editable fields */}
      <Section label="Details">
        <div className="grid grid-cols-2 gap-[var(--spacing-3)]">
          {/* Owner — editable */}
          <Field label="Owner">
            <OwnerSelector
              value={card.owner}
              onChange={(owner) => onCardUpdate?.(card.id, { owner })}
            />
          </Field>

          {/* Priority — editable */}
          <Field label="Priority">
            <PrioritySelector
              value={card.priority}
              onChange={(priority) => onCardUpdate?.(card.id, { priority })}
            />
          </Field>

          <Field label="Lane">
            <LaneSelector
              value={card.laneId}
              onChange={(laneId) => onCardUpdate?.(card.id, { laneId: laneId as LaneId })}
            />
          </Field>

          {card.weekNumber !== undefined && (
            <Field label="Week">
              <span className="text-[14px] text-[var(--color-text-primary)]">{weekLabels[card.weekNumber] ?? `W${card.weekNumber}`}</span>
            </Field>
          )}

          {/* Target date — editable */}
          <Field label="Target">
            <EditableDate
              value={card.targetDate ?? ""}
              onSave={(val) => onCardUpdate?.(card.id, { targetDate: val })}
            />
          </Field>

          {card.completedDate && (
            <Field label="Completed">
              <span className="text-[14px] tabular-nums text-[var(--color-state-success)]">{card.completedDate}</span>
            </Field>
          )}

          {card.channel && (
            <Field label="Channel"><span className="text-[14px] text-[var(--color-text-primary)]">{card.channel}</span></Field>
          )}
          {card.channelTier && (
            <Field label="Tier"><Badge variant={card.channelTier === "A" ? "accent" : "default"}>Tier {card.channelTier}</Badge></Field>
          )}
        </div>

        {/* URL — editable */}
        <div className="mt-[var(--spacing-2)]">
          <EditableTextField
            label="URL"
            value={card.url ?? ""}
            placeholder="https://…"
            onSave={(val) => onCardUpdate?.(card.id, { url: val })}
          />
        </div>

        {card.utmCampaign && (
          <div className="mt-[var(--spacing-2)]">
            <Field label="UTM Campaign">
              <span className="text-[14px] font-mono text-[var(--color-text-muted)] break-all leading-[20px]">{card.utmCampaign}</span>
            </Field>
          </div>
        )}
      </Section>

      {/* Tags — editable */}
      <Section label="Tags">
        <EditableTags
          tags={card.tags}
          onSave={(tags) => onCardUpdate?.(card.id, { tags })}
        />
      </Section>

      {/* Dependencies */}
      {(deps.length > 0 || dependents.length > 0) && (
        <Section label="Dependencies">
          {deps.length > 0 && (
            <div className="mb-[var(--spacing-2)]">
              <span className="block text-[14px] text-[var(--color-text-muted)] mb-[var(--spacing-1)]">Depends on ({deps.length})</span>
              <div className="space-y-[var(--spacing-1)]">
                {deps.map((d) => <DepRow key={d.id} card={d} />)}
              </div>
            </div>
          )}
          {dependents.length > 0 && (
            <div>
              <span className="block text-[14px] text-[var(--color-text-muted)] mb-[var(--spacing-1)]">Blocks ({dependents.length})</span>
              <div className="space-y-[var(--spacing-1)]">
                {dependents.map((d) => <DepRow key={d.id} card={d} />)}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Notes — editable */}
      <EditableTextArea
        label="Notes"
        value={card.notes}
        placeholder="Add notes…"
        onSave={(val) => onCardUpdate?.(card.id, { notes: val })}
      />

      {/* Actions */}
      <Section label="Actions">
        <div className="flex gap-[var(--spacing-2)]">
          {card.status !== "archived" ? (
            <button
              type="button"
              onClick={() => onArchive?.(card.id)}
              className={cn(
                "h-[32px] px-[var(--spacing-3)] rounded-[var(--radius-md)] text-[14px]",
                "border border-[var(--color-border-stroke)] text-[var(--color-text-secondary)]",
                "hover:bg-[var(--color-bg-hover)] transition-colors cursor-pointer",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
              )}
            >
              Archive
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onStatusChange(card.id, "planned")}
              className={cn(
                "h-[32px] px-[var(--spacing-3)] rounded-[var(--radius-md)] text-[14px]",
                "border border-[var(--color-border-stroke)] text-[var(--color-accent)]",
                "hover:bg-[var(--color-bg-hover)] transition-colors cursor-pointer",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
              )}
            >
              Unarchive
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (confirm("Delete this task permanently?")) onDelete?.(card.id);
            }}
            className={cn(
              "h-[32px] px-[var(--spacing-3)] rounded-[var(--radius-md)] text-[14px]",
              "border border-[var(--color-state-error)] text-[var(--color-state-error)]",
              "hover:bg-red-500/10 transition-colors cursor-pointer",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            )}
          >
            Delete
          </button>
        </div>
      </Section>
    </div>
  );
}

// ── Editable Components ─────────────────────────────────────

function EditableTextArea({ label, value, placeholder, onSave }: {
  label: string;
  value: string;
  placeholder: string;
  onSave: (val: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const save = useCallback(() => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  }, [draft, value, onSave]);

  return (
    <Section label={label}>
      {editing ? (
        <textarea
          ref={ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
          placeholder={placeholder}
          rows={3}
          className={cn(
            "w-full rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
            "px-[var(--spacing-3)] py-[var(--spacing-2)] text-[14px] leading-[20px] text-[var(--color-text-primary)]",
            "placeholder:text-[var(--color-text-muted)] resize-y",
            "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:border-transparent",
          )}
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          className={cn(
            "w-full text-left rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
            "text-[14px] leading-[20px] whitespace-pre-wrap cursor-pointer",
            "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            value ? "text-[var(--color-text-secondary)]" : "text-[var(--color-text-muted)] italic",
          )}
        >
          {value || placeholder}
        </button>
      )}
    </Section>
  );
}

function EditableTextField({ label, value, placeholder, onSave }: {
  label: string;
  value: string;
  placeholder: string;
  onSave: (val: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const save = useCallback(() => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  }, [draft, value, onSave]);

  return (
    <Field label={label}>
      {editing ? (
        <input
          ref={ref}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") { setDraft(value); setEditing(false); }
          }}
          placeholder={placeholder}
          className={cn(
            "w-full rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
            "px-[var(--spacing-2)] py-[var(--spacing-1)] text-[14px] text-[var(--color-text-primary)]",
            "placeholder:text-[var(--color-text-muted)]",
            "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:border-transparent",
          )}
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          className={cn(
            "text-left text-[14px] cursor-pointer",
            "hover:text-[var(--color-accent)] transition-colors duration-[120ms]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            value ? "text-[var(--color-text-primary)] break-all" : "text-[var(--color-text-muted)] italic",
          )}
        >
          {value || placeholder}
        </button>
      )}
    </Field>
  );
}

function EditableDate({ value, onSave }: { value: string; onSave: (val: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const save = useCallback(() => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  }, [draft, value, onSave]);

  if (editing) {
    return (
      <input
        ref={ref}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        className={cn(
          "w-full rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
          "px-[var(--spacing-2)] py-[var(--spacing-1)] text-[14px] text-[var(--color-text-primary)]",
          "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:border-transparent",
        )}
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className={cn(
        "text-left text-[14px] tabular-nums cursor-pointer",
        "hover:text-[var(--color-accent)] transition-colors duration-[120ms]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
        value ? "text-[var(--color-text-primary)]" : "text-[var(--color-text-muted)] italic",
      )}
    >
      {value || "Set date…"}
    </button>
  );
}

function OwnerSelector({ value, onChange }: { value: Owner; onChange: (owner: Owner) => void }) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={cn(
          "cursor-pointer hover:opacity-80 transition-opacity duration-[120ms]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] rounded-full",
        )}
      >
        <OwnerTag owner={value} />
      </button>
    );
  }

  return (
    <div className="flex gap-[var(--spacing-1)] flex-wrap">
      {owners.map((o) => (
        <button
          key={o}
          onClick={() => { onChange(o); setOpen(false); }}
          className={cn(
            "cursor-pointer transition-all duration-[120ms] rounded-full",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            o === value && "ring-2 ring-[var(--color-accent)]",
          )}
        >
          <OwnerTag owner={o} />
        </button>
      ))}
    </div>
  );
}

function PrioritySelector({ value, onChange }: { value: Priority; onChange: (p: Priority) => void }) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center gap-[var(--spacing-1)] text-[14px] text-[var(--color-text-primary)] cursor-pointer",
          "hover:opacity-80 transition-opacity duration-[120ms]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] rounded-[var(--radius-md)]",
        )}
      >
        <PriorityDot priority={value} />
        <span className="capitalize">{value}</span>
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--spacing-1)]">
      {priorities.map((p) => (
        <button
          key={p}
          onClick={() => { onChange(p); setOpen(false); }}
          className={cn(
            "flex items-center gap-[var(--spacing-1)] text-[14px] text-[var(--color-text-primary)]",
            "cursor-pointer rounded-[var(--radius-md)] px-[var(--spacing-2)] py-[var(--spacing-1)]",
            "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            p === value && "bg-[var(--color-bg-hover)]",
          )}
        >
          <PriorityDot priority={p} />
          <span className="capitalize">{p}</span>
        </button>
      ))}
    </div>
  );
}

function EditableTags({ tags, onSave }: { tags: string[]; onSave: (tags: string[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { if (adding) ref.current?.focus(); }, [adding]);

  const addTag = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onSave([...tags, trimmed]);
    }
    setDraft("");
    setAdding(false);
  }, [draft, tags, onSave]);

  const removeTag = useCallback((tag: string) => {
    onSave(tags.filter((t) => t !== tag));
  }, [tags, onSave]);

  return (
    <div className="flex flex-wrap gap-[var(--spacing-1)] items-center">
      {tags.map((t) => (
        <span key={t} className="group inline-flex items-center gap-[2px]">
          <Badge>{t}</Badge>
          <button
            onClick={() => removeTag(t)}
            className={cn(
              "hidden group-hover:inline-flex items-center justify-center",
              "h-[14px] w-[14px] rounded-full text-[10px] leading-none",
              "bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-state-error)]",
              "cursor-pointer transition-colors duration-[120ms]",
            )}
            aria-label={`Remove tag ${t}`}
          >
            ×
          </button>
        </span>
      ))}
      {adding ? (
        <input
          ref={ref}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={addTag}
          onKeyDown={(e) => {
            if (e.key === "Enter") addTag();
            if (e.key === "Escape") { setDraft(""); setAdding(false); }
          }}
          placeholder="Tag name…"
          className={cn(
            "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
            "px-[var(--spacing-2)] py-[1px] text-[12px] text-[var(--color-text-primary)] w-[80px]",
            "placeholder:text-[var(--color-text-muted)]",
            "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:border-transparent",
          )}
        />
      ) : (
        <button
          onClick={() => setAdding(true)}
          className={cn(
            "inline-flex items-center gap-[2px] rounded-full px-[7px] py-[1px]",
            "text-[11px] leading-[16px] font-medium text-[var(--color-text-muted)]",
            "border border-dashed border-[var(--color-border-stroke)]",
            "hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]",
            "cursor-pointer transition-colors duration-[120ms]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
          )}
        >
          + Add
        </button>
      )}
    </div>
  );
}

// ── Editable Title ──────────────────────────────────────────

function EditableTitle({ value, onSave }: { value: string; onSave: (val: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const save = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
  }, [draft, value, onSave]);

  if (editing) {
    return (
      <input
        ref={ref}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        className={cn(
          "flex-1 min-w-0 text-[14px] font-semibold text-[var(--color-text-primary)]",
          "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
          "px-[var(--spacing-2)] py-[var(--spacing-1)]",
          "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:border-transparent",
        )}
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className={cn(
        "text-[14px] font-semibold text-[var(--color-text-primary)] truncate cursor-pointer text-left",
        "hover:text-[var(--color-accent)] transition-colors duration-[120ms]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] rounded-[var(--radius-md)]",
      )}
    >
      {value}
    </button>
  );
}

// ── Lane Selector ───────────────────────────────────────────

const laneEntries: Array<{ id: string; label: string }> = [
  { id: "objective_kpis", label: "Objectives & KPIs" },
  { id: "offer_pricing", label: "Offer & Pricing" },
  { id: "channels", label: "Channels" },
  { id: "commercial_pipeline", label: "Commercial Pipeline" },
  { id: "weekly_content", label: "Weekly Content" },
  { id: "revops_tracking", label: "RevOps & Tracking" },
  { id: "feedback_loop", label: "Feedback Loop" },
];

function LaneSelector({ value, onChange }: { value: string; onChange: (laneId: string) => void }) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={cn(
          "text-[14px] text-[var(--color-text-primary)] cursor-pointer text-left",
          "hover:text-[var(--color-accent)] transition-colors duration-[120ms]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] rounded-[var(--radius-md)]",
        )}
      >
        {laneLabels[value] ?? value}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--spacing-1)]">
      {laneEntries.map((lane) => (
        <button
          key={lane.id}
          onClick={() => { onChange(lane.id); setOpen(false); }}
          className={cn(
            "text-[14px] text-[var(--color-text-primary)] text-left",
            "cursor-pointer rounded-[var(--radius-md)] px-[var(--spacing-2)] py-[var(--spacing-1)]",
            "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            lane.id === value && "bg-[var(--color-bg-hover)]",
          )}
        >
          {lane.label}
        </button>
      ))}
    </div>
  );
}

// ── Shared Layout Components ────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <span className="block text-[14px] font-semibold text-[var(--color-text-muted)] uppercase tracking-[0.04em] mb-[var(--spacing-2)]">
        {label}
      </span>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="block text-[14px] text-[var(--color-text-muted)] mb-[var(--spacing-0_5)]">{label}</span>
      {children}
    </div>
  );
}

function DepRow({ card }: { card: CardType }) {
  return (
    <div className="flex items-center gap-[var(--spacing-2)] rounded-[var(--radius-md)] bg-[var(--color-bg-hover)] border border-[var(--color-border-stroke)] px-[var(--spacing-3)] py-[var(--spacing-1_5)]">
      <StatusChip status={card.status} />
      <span className="text-[14px] text-[var(--color-text-primary)] truncate flex-1">{card.title}</span>
      <OwnerTag owner={card.owner} />
    </div>
  );
}
